/**
 * KAN-871: шаги подключения личного Telegram в профиле приложения менеджера:
 * номер -> код из Telegram -> облачный пароль (если включён). Только рендер,
 * состояние и запросы в usePersonalTelegram.
 */
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { ProfileField } from '@/components/mobile/ProfileField';
import type { PersonalTelegramControl } from '@/hooks/usePersonalTelegram';
import { PERSONAL_TELEGRAM_TEXTS as T } from '@/lib/personalTelegramTexts';

const PRIMARY =
  'flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-medium text-white active:bg-blue-700 disabled:opacity-60';
const SECONDARY = 'h-11 w-full rounded-xl border border-gray-300 text-sm text-gray-700 disabled:opacity-60';

function SubmitButton({ busy, children }: { busy: boolean; children: string }): JSX.Element {
  return (
    <button type="submit" disabled={busy} className={PRIMARY}>
      {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function MobilePersonalTelegramConnect({ tg }: { tg: PersonalTelegramControl }): JSX.Element {
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (tg.step === 'phone') tg.requestCode();
    else if (tg.step === 'code') tg.submitCode();
    else if (tg.step === 'password') tg.submitPassword();
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3">
      <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600" data-testid="personal-telegram-hints">
        {T.hints.map((hint) => (
          <li key={hint}>{hint}</li>
        ))}
      </ul>
      {tg.step === 'phone' && (
        <>
          <ProfileField
            idPrefix="personal-telegram"
            name="phone"
            label={T.phoneLabel}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 900 000-00-00"
            maxLength={32}
            value={tg.phone}
            onChange={tg.setPhone}
          />
          <SubmitButton busy={tg.busy}>{T.getCode}</SubmitButton>
        </>
      )}
      {tg.step === 'code' && (
        <>
          <ProfileField
            idPrefix="personal-telegram"
            name="code"
            label={T.codeLabel}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={16}
            value={tg.code}
            onChange={tg.setCode}
          />
          <SubmitButton busy={tg.busy}>{T.confirm}</SubmitButton>
        </>
      )}
      {tg.step === 'password' && (
        <>
          <ProfileField
            idPrefix="personal-telegram"
            name="password"
            label={T.passwordLabel}
            type="password"
            autoComplete="current-password"
            hint={T.passwordHint}
            value={tg.password}
            onChange={tg.setPassword}
          />
          <SubmitButton busy={tg.busy}>{T.confirm}</SubmitButton>
        </>
      )}
      <button type="button" onClick={tg.cancelConnect} disabled={tg.busy} className={SECONDARY}>
        {T.cancel}
      </button>
    </form>
  );
}
